// utils/offlineQueue.js
// IndexedDB-based offline queue for photos captured without network connectivity.
// Photos are stored as native Blobs (not base64) so 100+ shots fit comfortably
// on a tablet without exhausting IndexedDB quota. Items are retried automatically
// when connectivity is restored, both from the foreground (via SyncContext) and
// from the background sync event handler in sw.js.

import { mayUploadNow, loadUploadSettings } from './syncWindow';

const DB_NAME = 'photoapp-offline';
const DB_VERSION = 2;
const STORE_PHOTOS = 'pending_photos';
const STORE_METADATA = 'pending_metadata';

let _db = null;

async function getDB() {
  if (_db) return _db;
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_PHOTOS)) {
        const store = db.createObjectStore(STORE_PHOTOS, { keyPath: 'id' });
        store.createIndex('order_id', 'order_id');
        store.createIndex('session_id', 'session_id');
        store.createIndex('status', 'status');
      }
      if (!db.objectStoreNames.contains(STORE_METADATA)) {
        db.createObjectStore(STORE_METADATA, { keyPath: 'id' });
      }
    };
    req.onsuccess = (e) => { _db = e.target.result; resolve(_db); };
    req.onerror = (e) => reject(e.target.error);
  });
}

function txStore(db, storeName, mode = 'readonly') {
  return db.transaction(storeName, mode).objectStore(storeName);
}

// ── Pending Photo Queue ───────────────────────────────────────────────────────

export async function queuePhoto({
  id, session_id, order_id, step_name, step_index, is_na,
  device_id, orientation, blob, mimeType,
}) {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const store = txStore(db, STORE_PHOTOS, 'readwrite');
    const item = {
      id,
      session_id, order_id, step_name, step_index,
      is_na: is_na || false,
      device_id, orientation,
      blob: blob || null,
      mimeType: mimeType || (blob && blob.type) || 'image/jpeg',
      size_bytes: blob?.size || 0,
      status: 'pending',
      attempts: 0,
      created_at: new Date().toISOString(),
      last_attempt: null,
    };
    const req = store.put(item);
    req.onsuccess = () => resolve(item);
    req.onerror = (e) => reject(e.target.error);
  });
}

export async function getPendingPhotos() {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const store = txStore(db, STORE_PHOTOS);
    const req = store.index('status').getAll('pending');
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror = (e) => reject(e.target.error);
  });
}

export async function getFailedPhotos() {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const store = txStore(db, STORE_PHOTOS);
    const req = store.index('status').getAll('failed');
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror = (e) => reject(e.target.error);
  });
}

// Exponential backoff between retries: 30s, 1m, 2m, 4m … capped at 15m.
// 'failed' (≥5 attempts) items sit at the cap but are never abandoned — on a
// factory connection that drops uploads mid-body, every photo must eventually land.
export function retryDelayMs(attempts) {
  return Math.min(30_000 * 2 ** Math.max(0, (attempts || 0) - 1), 15 * 60_000);
}

function isDueForRetry(item, force) {
  if (force || !item.last_attempt) return true;
  return Date.now() - new Date(item.last_attempt).getTime() >= retryDelayMs(item.attempts);
}

// Everything that still needs to reach the server: pending items plus 'failed'
// ones (the status is a UI signal, not a terminal state). Respects per-item
// backoff unless force (the "Sync now" button) is set.
async function getRetryablePhotos(force) {
  const [pending, failed] = await Promise.all([getPendingPhotos(), getFailedPhotos()]);
  return [...pending, ...failed].filter((item) => isDueForRetry(item, force));
}

export async function getAllQueuedPhotos() {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const store = txStore(db, STORE_PHOTOS);
    const req = store.getAll();
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror = (e) => reject(e.target.error);
  });
}

export async function updatePhotoStatus(id, status, error = null) {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const store = txStore(db, STORE_PHOTOS, 'readwrite');
    const getReq = store.get(id);
    getReq.onsuccess = (e) => {
      const item = e.target.result;
      if (!item) return resolve(null);
      item.status = status;
      item.attempts = (item.attempts || 0) + 1;
      item.last_attempt = new Date().toISOString();
      if (error) item.last_error = String(error);
      const putReq = store.put(item);
      putReq.onsuccess = () => resolve(item);
      putReq.onerror = (e) => reject(e.target.error);
    };
    getReq.onerror = (e) => reject(e.target.error);
  });
}

export async function removeQueuedPhoto(id) {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const store = txStore(db, STORE_PHOTOS, 'readwrite');
    const req = store.delete(id);
    req.onsuccess = () => resolve();
    req.onerror = (e) => reject(e.target.error);
  });
}

export async function getQueueStats() {
  const db = await getDB();
  return new Promise((resolve) => {
    const store = txStore(db, STORE_PHOTOS);
    const all = store.getAll();
    all.onsuccess = (e) => {
      const items = e.target.result;
      const totalBytes = items.reduce((sum, i) => sum + (i.size_bytes || 0), 0);
      resolve({
        total: items.length,
        pending: items.filter(i => i.status === 'pending').length,
        failed: items.filter(i => i.status === 'failed').length,
        synced: items.filter(i => i.status === 'synced').length,
        bytes: totalBytes,
      });
    };
    all.onerror = () => resolve({ total: 0, pending: 0, failed: 0, synced: 0, bytes: 0 });
  });
}

// "Sync now" wipes the retry backoff: every item is due again and counts as a
// fresh first attempt afterwards, so the automatic retries pick up quickly too.
export async function resetBackoff() {
  const db = await getDB();
  return new Promise((resolve) => {
    const store = txStore(db, STORE_PHOTOS, 'readwrite');
    const req = store.openCursor();
    req.onsuccess = (e) => {
      const cursor = e.target.result;
      if (!cursor) return resolve();
      const item = cursor.value;
      if (item.status === 'pending' || item.status === 'failed') {
        cursor.update({ ...item, status: 'pending', attempts: 0, last_attempt: null });
      }
      cursor.continue();
    };
    req.onerror = () => resolve();
  });
}

// ── Upload settings for the service worker ────────────────────────────────────
// The service worker cannot read localStorage, so the site's upload settings
// (sync window) are mirrored into the metadata store under a fixed id. Its
// status is not 'pending', so the order/session replay below never touches it.
const UPLOAD_SETTINGS_ID = '__upload_settings__';

export async function saveUploadSettingsForWorker(settings) {
  try {
    const db = await getDB();
    await new Promise((resolve, reject) => {
      const store = txStore(db, STORE_METADATA, 'readwrite');
      const req = settings
        ? store.put({ id: UPLOAD_SETTINGS_ID, kind: 'config', status: 'config', settings })
        : store.delete(UPLOAD_SETTINGS_ID);
      req.onsuccess = () => resolve();
      req.onerror = (e) => reject(e.target.error);
    });
  } catch {
    // Without the mirror the worker syncs as before (no window); the foreground
    // queue still honours the window from localStorage.
  }
}

// ── Pending order / session queue (offline-created) ───────────────────────────
// Orders and sessions normally need the server, but offline the photographer must
// still be able to start a session. We generate their ids on the device, queue the
// create here, and replay them (idempotently) before the photos they belong to.

async function queueMeta(item) {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const store = txStore(db, STORE_METADATA, 'readwrite');
    const req = store.put({ status: 'pending', attempts: 0, created_at: new Date().toISOString(), ...item });
    req.onsuccess = () => resolve(item);
    req.onerror = (e) => reject(e.target.error);
  });
}

export function queueOrder({ id, order_number, product_name, factory }) {
  return queueMeta({ id, kind: 'order', payload: { id, order_number, product_name, factory: factory || null } });
}

export function queueSession({ id, order_id }) {
  return queueMeta({ id, kind: 'session', payload: { id, order_id } });
}

async function getPendingMeta() {
  const db = await getDB();
  return new Promise((resolve) => {
    const store = txStore(db, STORE_METADATA);
    const req = store.getAll();
    req.onsuccess = (e) => {
      const items = (e.target.result || []).filter((i) => i.status === 'pending');
      // Orders must be created before the sessions that reference them.
      items.sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'order' ? -1 : 1));
      resolve(items);
    };
    req.onerror = () => resolve([]);
  });
}

async function removeMeta(id) {
  const db = await getDB();
  return new Promise((resolve) => {
    const store = txStore(db, STORE_METADATA, 'readwrite');
    const req = store.delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
  });
}

// Replay queued orders then sessions. Endpoints are idempotent on the client id,
// so a partial failure is safe to retry. Returns the number flushed.
export async function processPendingMeta(apiInstance) {
  const items = await getPendingMeta();
  let flushed = 0;
  for (const item of items) {
    try {
      const endpoint = item.kind === 'order' ? '/api/orders' : '/api/sessions';
      await apiInstance.post(endpoint, item.payload, { timeout: 30000 });
      await removeMeta(item.id);
      flushed++;
    } catch {
      // Leave it pending; the next sync round retries.
    }
  }
  return flushed;
}

// ── Auto-cleanup: remove synced items older than retention period ─────────────

export async function cleanupSynced(retentionDays = 7) {
  const db = await getDB();
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - retentionDays);

  return new Promise((resolve) => {
    const store = txStore(db, STORE_PHOTOS, 'readwrite');
    const req = store.openCursor();
    let removed = 0;
    req.onsuccess = (e) => {
      const cursor = e.target.result;
      if (!cursor) return resolve(removed);
      const item = cursor.value;
      if (item.status === 'synced' && new Date(item.created_at) < cutoff) {
        cursor.delete();
        removed++;
      }
      cursor.continue();
    };
    req.onerror = () => resolve(removed);
  });
}

// ── Upload processor ──────────────────────────────────────────────────────────

// Which due items may go out now. Outside the sync window only N/A markers
// (no file, a few bytes) pass; photo files are held, never dropped. A photo
// held for more than a day goes out anyway (see heldTooLong).
export function selectForUpload(dueItems, windowOpen, now = new Date()) {
  const upload = windowOpen ? dueItems : dueItems.filter((item) => mayUploadNow(item, windowOpen, now));
  return { upload, held: dueItems.length - upload.length };
}

// Move photos from the web queue to the native uploader (tablet shell), which
// keeps uploading with the app closed. Used when the sync window opens for the
// photos that were held here. A photo leaves the web queue only after the
// native side has accepted it, so it is never in neither queue; and once it
// has left, the web paths cannot send it a second time. A bridge failure
// leaves the photo here and the web upload takes it. One hand-over at a time.
let handOverRun = null;
export function handOverToNative(enqueue, { windowOpen = true, now = new Date() } = {}) {
  if (handOverRun) return handOverRun;
  handOverRun = (async () => {
    const [pending, failed] = await Promise.all([getPendingPhotos(), getFailedPhotos()]);
    let moved = 0;
    for (const item of [...pending, ...failed]) {
      if (item.is_na || !item.blob || !mayUploadNow(item, windowOpen, now)) continue;
      try {
        await enqueue(item);
      } catch {
        continue;
      }
      await removeQueuedPhoto(item.id);
      moved++;
    }
    return moved;
  })().finally(() => { handOverRun = null; });
  return handOverRun;
}

/**
 * Process the upload queue (pending + failed items).
 * Called automatically when online, or manually via "Sync now" (force=true
 * skips the per-item backoff so a tap retries everything immediately).
 * windowOpen=false (outside the factory's sync window) holds photo files back;
 * orders, sessions and N/A markers are small metadata and always go through,
 * and so does a photo that has been held for more than a day.
 * Returns { uploaded, failed, held, lastError }.
 */
export async function processPendingQueue(apiInstance, onProgress, { force = false, windowOpen = true, now = new Date() } = {}) {
  // Offline-created orders/sessions must reach the server before their photos,
  // otherwise the photo upload fails the foreign-key check.
  await processPendingMeta(apiInstance);

  const { upload: pending, held } = selectForUpload(await getRetryablePhotos(force), windowOpen, now);
  if (pending.length === 0) return { uploaded: 0, failed: 0, held, lastError: null };

  let uploaded = 0;
  let failed = 0;
  let lastError = null;

  for (const item of pending) {
    try {
      onProgress?.({ current: uploaded + failed + 1, total: pending.length, item });

      const formData = new FormData();
      formData.append('id', item.id); // stable id → server dedupes if drained twice
      formData.append('session_id', item.session_id);
      formData.append('order_id', item.order_id);
      formData.append('step_name', item.step_name);
      formData.append('step_index', String(item.step_index));
      formData.append('is_na', item.is_na ? '1' : '0');
      formData.append('device_id', item.device_id || '');
      formData.append('orientation', item.orientation || 'landscape');
      formData.append('department', 'production');

      if (!item.is_na) {
        // New format: native Blob. Legacy format: imageDataUrl (base64).
        const blob = item.blob || (item.imageDataUrl ? dataURLtoBlob(item.imageDataUrl) : null);
        if (blob) formData.append('photo', blob, `${item.id}.jpg`);
      }

      await apiInstance.post('/api/photos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });

      await updatePhotoStatus(item.id, 'synced');
      uploaded++;
    } catch (err) {
      const attempts = (item.attempts || 0) + 1;
      // ≥5 attempts shows as 'failed' in the sync bar, but the item stays in
      // the retry loop (getRetryablePhotos) — it is never abandoned.
      const newStatus = attempts >= 5 ? 'failed' : 'pending';
      await updatePhotoStatus(item.id, newStatus, err?.message);
      failed++;
      lastError = err?.message || String(err);
    }
  }

  return { uploaded, failed, held, lastError };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function dataURLtoBlob(dataURL) {
  const [header, data] = dataURL.split(',');
  const mimeMatch = header.match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const binary = atob(data);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i);
  return new Blob([array], { type: mime });
}

export function formatBytes(bytes) {
  if (!bytes) return '0 KB';
  const mb = bytes / (1024 * 1024);
  if (mb < 1) return `${Math.round(bytes / 1024)} KB`;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

export function estimateStorageUsage(photos) {
  let totalBytes = 0;
  for (const p of photos) {
    if (p.blob?.size) totalBytes += p.blob.size;
    else if (p.size_bytes) totalBytes += p.size_bytes;
    else if (p.imageDataUrl) totalBytes += Math.round((p.imageDataUrl.length * 3) / 4);
  }
  return formatBytes(totalBytes);
}

// Ask the browser (if it can — Chrome ≥ 49) to register a background sync
// so the OS will retry the queue when connectivity returns, even if the tab
// is in the background or closed.
export async function requestBackgroundSync() {
  requestPeriodicSync();
  try {
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      const reg = await navigator.serviceWorker.ready;
      await reg.sync.register('sync-photos');
      return true;
    }
  } catch {
    // Quietly ignore: foreground SyncContext also retries on 'online' event.
  }
  return false;
}

// With a sync window, photos wait for hours and the one-shot sync above may be
// long gone when the window opens. Where the browser offers periodic background
// sync (installed PWA/TWA), let it wake the worker now and then. Without a
// window nothing is registered: behaviour stays as it was.
async function requestPeriodicSync() {
  try {
    if (!Number(loadUploadSettings()?.sync_window_enabled)) return;
    if (!('serviceWorker' in navigator)) return;
    const reg = await navigator.serviceWorker.ready;
    if (!reg.periodicSync) return;
    await reg.periodicSync.register('sync-photos', { minInterval: 15 * 60 * 1000 });
  } catch {
    // Not granted or not supported: the page timer and one-shot sync remain.
  }
}
